import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { courses as coursesApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PaymentMethodModal from "@/components/PaymentMethodModal";
import ShareButtons from "@/components/ShareButtons";
import { Check, Clock, BarChart3, ArrowLeft, Lock, Play } from "lucide-react";

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    coursesApi.get(id).then(setCourse).catch(() => setCourse(null));
  }, [id]);

  if (!course) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-zinc-500">Cargando...</div>
      </div>
    );
  }

  const owned = user?.purchased_courses?.includes(course.id);
  const handleBuy = () => setPayOpen(true);

  return (
    <main data-testid="course-detail-page" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-20 md:pb-32">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="aspect-video bg-zinc-100 mb-8 overflow-hidden">
              <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#4F46E5] mb-3">
              {course.level} · {course.duration}
            </p>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
              {course.title}
            </h1>
            <p className="text-lg text-zinc-700 leading-relaxed mb-10">{course.description}</p>

            <h2 className="font-display font-bold text-2xl mb-5">Contenido del curso</h2>
            <div className="border border-zinc-200 divide-y divide-zinc-200">
              {course.modules.map((m, i) => (
                <div
                  key={`${course.id}-mod-${i}`}
                  data-testid={`module-${i}`}
                  className="flex items-center justify-between p-5 hover:bg-zinc-50 transition"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center font-mono text-sm">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <div className="font-display font-semibold">{m.title}</div>
                      <div className="text-xs text-zinc-500">
                        {m.lessons} lecciones · {m.duration}
                      </div>
                    </div>
                  </div>
                  {owned ? (
                    <Play className="w-5 h-5 text-[#4F46E5]" />
                  ) : (
                    <Lock className="w-4 h-4 text-zinc-400" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-zinc-50 border border-zinc-200 p-8">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                Pago único · Acceso de por vida
              </div>
              <div className="font-display font-black text-5xl mt-2 mb-6">
                ${course.price}
                <span className="text-base text-zinc-500 font-normal ml-2">USD</span>
              </div>

              {owned ? (
                <button
                  onClick={() => navigate("/dashboard")}
                  data-testid="goto-content-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 font-semibold transition"
                >
                  Ya lo compraste · Ir al contenido
                </button>
              ) : (
                <button
                  onClick={handleBuy}
                  data-testid="course-detail-buy-btn"
                  className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white py-4 font-semibold transition"
                >
                  Comprar ahora
                </button>
              )}

              <ul className="mt-8 space-y-3">
                {(course.highlights || []).map((h, i) => (
                  <li key={`${course.id}-h-${i}`} className="flex items-start gap-2 text-sm text-zinc-700">
                    <Check className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-8 border-t border-zinc-300 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Clock className="w-4 h-4 text-zinc-500 mb-1" />
                  <div className="font-semibold">{course.duration}</div>
                  <div className="text-xs text-zinc-500">de contenido</div>
                </div>
                <div>
                  <BarChart3 className="w-4 h-4 text-zinc-500 mb-1" />
                  <div className="font-semibold">{course.level}</div>
                  <div className="text-xs text-zinc-500">nivel</div>
                </div>
              </div>

              <ShareButtons
                title={course.title}
                url={`https://kursosonline.com/curso/${course.id}`}
              />
            </div>
          </aside>
        </div>
      </div>
      <PaymentMethodModal
        open={payOpen}
        course={course}
        onClose={() => setPayOpen(false)}
      />
    </main>
  );
};

export default CourseDetail;
